// src/hooks/useGrammarQuest.js
// Lógica completa del juego Grammar Word Quest
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { GRAMMAR_QUEST_QUESTIONS, CEFR_LEVELS } from '../data/grammarQuestData';
const STORAGE_KEYS = {
  collection: 'gwq_collection_v1',
  progress:   'gwq_progress_v1',
  completed:  'gwq_completed_v1',
};


// true  → el punto solo se da si aciertas al primer intento
// false → se da el punto aunque aciertes tras la pista (comportamiento anterior)
const POINT_ONLY_ON_FIRST_TRY = false;


// ─────────────────────────────────────────────
// Web Audio API — Sonidos procedimentales
// ─────────────────────────────────────────────
function playQuestSound(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const master = ctx.createGain();
    master.connect(ctx.destination);
    master.gain.setValueAtTime(0.18, ctx.currentTime);


    if (type === 'correct') {
      // Chime armónico: Do → Mi → Sol (arpegio ascendente)
      [[523.25, 0], [659.25, 0.12], [783.99, 0.24]].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(master);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.5, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.35);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.36);
      });
    } else if (type === 'wrong') {
      // Tono grave suave — no punitivo
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(master);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(330, ctx.currentTime);
      osc.frequency.linearRampToValueAtTime(220, ctx.currentTime + 0.2);
      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.26);
    } else if (type === 'unlock') {
      // Fanfarria breve de desbloqueo
      [[523.25, 0], [659.25, 0.1], [783.99, 0.2], [1046.5, 0.3]].forEach(([freq, delay]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(master);
        osc.type = 'sine';
        osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.45, ctx.currentTime + delay);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.4);
        osc.start(ctx.currentTime + delay);
        osc.stop(ctx.currentTime + delay + 0.41);
      });
    } else if (type === 'hint') {
      // Ping suave para la pista
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain); gain.connect(master);
      osc.type = 'sine';
      osc.frequency.value = 440;
      gain.gain.setValueAtTime(0.25, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.2);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.21);
    }


    setTimeout(() => {
      try { ctx.close(); } catch (_) { /* noop */ }
    }, 2000);
  } catch (_) {
    // Web Audio no disponible — silencioso
  }
}


// ─────────────────────────────────────────────
// Persistencia
// ─────────────────────────────────────────────
function loadCollection() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEYS.collection)) || []; }
  catch { return []; }
}
function saveCollection(col) {
  try { localStorage.setItem(STORAGE_KEYS.collection, JSON.stringify(col)); } catch { /* noop */ }
}
function loadLevelProgress(level) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.progress)) || {};
    return all[level] || { currentQ: 0, score: 0 };
  } catch { return { currentQ: 0, score: 0 }; }
}
function saveLevelProgress(level, data) {
  try {
    const all = JSON.parse(localStorage.getItem(STORAGE_KEYS.progress)) || {};
    all[level] = data;
    localStorage.setItem(STORAGE_KEYS.progress, JSON.stringify(all));
  } catch { /* noop */ }
}
function loadCompleted() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEYS.completed)) || {}; }
  catch { return {}; }
}
function saveCompleted(data) {
  try { localStorage.setItem(STORAGE_KEYS.completed, JSON.stringify(data)); } catch { /* noop */ }
}


// ─────────────────────────────────────────────
// Hook principal
// ─────────────────────────────────────────────
export function useGrammarQuest(activeSkin = 'bribark') {
  const [selectedLevel, setSelectedLevel] = useState(
  () => (CEFR_LEVELS.find((l) => l.available) || {}).id || 'A2'
);
  const questions = GRAMMAR_QUEST_QUESTIONS[selectedLevel] || [];
  const total = questions.length;


  // --- Progreso de sesión ---
  const [currentQ, setCurrentQ] = useState(0);
  const [score, setScore] = useState(0);
  const [attempts, setAttempts] = useState(0); // intentos en pregunta actual
  const [selectedOption, setSelectedOption] = useState(null);
  const [phase, setPhase] = useState('playing'); // 'playing' | 'correct' | 'hint' | 'revealed' | 'finished'
  const [collection, setCollection] = useState(loadCollection);
  const [showCollection, setShowCollection] = useState(false);
  const [showFinalChallenge, setShowFinalChallenge] = useState(false);
  const [mascotMessage, setMascotMessage] = useState('');
  const mascotTimerRef = useRef(null);
  const unlockTimerRef = useRef(null);


  // Limpiar temporizadores pendientes al desmontar
  useEffect(() => {
    return () => {
      if (mascotTimerRef.current) clearTimeout(mascotTimerRef.current);
      if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
    };
  }, []);


  // Cargar progreso guardado al montar / cambiar nivel
  useEffect(() => {
    const saved = loadLevelProgress(selectedLevel);
    const completedMap = loadCompleted();
    if (completedMap[selectedLevel]) {
      // Ya completó el nivel — arranca en modo colección/final
      setCurrentQ(total);
      setPhase('finished');
    } else {
      setCurrentQ(saved.currentQ || 0);
      setScore(saved.score || 0);
      setPhase('playing');
    }
    setAttempts(0);
    setSelectedOption(null);
  }, [selectedLevel, total]);


  // Persistir progreso de nivel
  useEffect(() => {
    if (phase !== 'finished' && currentQ >= 0) {
      saveLevelProgress(selectedLevel, { currentQ, score });
    }
  }, [currentQ, score, phase, selectedLevel]);


  // ─── Mensaje de mascota ───
  const showMascot = useCallback((msg, duration = 3500) => {
    if (mascotTimerRef.current) clearTimeout(mascotTimerRef.current);
    setMascotMessage(msg);
    mascotTimerRef.current = setTimeout(() => setMascotMessage(''), duration);
  }, []);


  // ─── Pronunciación TTS ───
  const speak = useCallback((text) => {
    if (!('speechSynthesis' in window)) return;
    window.speechSynthesis.cancel();
    const utt = new SpeechSynthesisUtterance(text);
    utt.lang = 'en-US';
    utt.rate = 0.85;
    utt.pitch = 1.0;
    window.speechSynthesis.speak(utt);
  }, []);


  // ─── Comprobar respuesta ───
  const checkAnswer = useCallback((option) => {
    if (phase !== 'playing') return;


    const q = questions[currentQ];
    if (!q) return;


    setSelectedOption(option);


    // Desbloqueo de palabra calculado una sola vez, fuera de setCollection
    const word = q.collected_word;
    const isNewWord = !collection.includes(word);
    const addWordToCollection = () => {
      if (!isNewWord) return;
      const updated = [...collection, word];
      setCollection(updated);
      saveCollection(updated);
    };


    if (option === q.answer) {
      // CORRECTA
      playQuestSound('correct');
      if (!POINT_ONLY_ON_FIRST_TRY || attempts === 0) {
        setScore(score + 1);
      }
      setPhase('correct');


      addWordToCollection();
      if (isNewWord) {
        if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current);
        unlockTimerRef.current = setTimeout(() => {
          playQuestSound('unlock');
          showMascot(`¡Desbloqueaste "${word}"! 🌟 ¡A tu colección!`);
        }, 600);
      }


      if (attempts === 0) {
        showMascot('¡Perfecto! 🎉 Primer intento → +1 punto');
      } else {
        showMascot('¡Muy bien! ✅ ¡Lo lograste!');
      }


    } else {
      // INCORRECTA
      playQuestSound('wrong');
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);


      if (newAttempts === 1) {
        // 1er error → pista
        setPhase('hint');
        playQuestSound('hint');
        showMascot(`💡 Pista: ${q.tip}`);
      } else {
        // 2do error → revelar respuesta
        setPhase('revealed');
        // Desbloquear igualmente para seguir aprendiendo
        addWordToCollection();
        showMascot(`La respuesta es "${q.answer}". ¡Sigue adelante! 💪`);
      }
    }
  }, [phase, questions, currentQ, score, attempts, collection, showMascot]);


  // ─── Siguiente pregunta ───
  const nextQuestion = useCallback(() => {
    const next = currentQ + 1;
    if (next >= total) {
      // Fin del nivel
      setPhase('finished');
      setShowFinalChallenge(true);
      const completedMap = loadCompleted();
      completedMap[selectedLevel] = true;
      saveCompleted(completedMap);
      showMascot(`¡Completaste el nivel ${selectedLevel}! 🏆 ¡Increíble trabajo!`, 5000);
    } else {
      setCurrentQ(next);
      setAttempts(0);
      setSelectedOption(null);
      setPhase('playing');
    }
  }, [currentQ, total, selectedLevel, showMascot]);


  // ─── Reintentar tras pista ───
  const retryAfterHint = useCallback(() => {
    setSelectedOption(null);
    setPhase('playing');
  }, []);


  // ─── Reiniciar nivel ───
  const resetLevel = useCallback(() => {
    saveLevelProgress(selectedLevel, { currentQ: 0, score: 0 });
    const completedMap = loadCompleted();
    delete completedMap[selectedLevel];
    saveCompleted(completedMap);
    setCurrentQ(0);
    setScore(0);
    setAttempts(0);
    setSelectedOption(null);
    setPhase('playing');
    setShowFinalChallenge(false);
    showMascot('¡A practicar de nuevo! 🚀');
  }, [selectedLevel, showMascot]);


  const currentQuestion = questions[currentQ] || null;
  const progressPct = total > 0 ? Math.round((currentQ / total) * 100) : 0;
  const collectedCount = collection.length;
  const isLevelCompleted = useMemo(() => loadCompleted()[selectedLevel] || false, [selectedLevel, phase]);


  return {
    // Estado
    selectedLevel,
    setSelectedLevel,
    currentQuestion,
    currentQ,
    total,
    score,
    attempts,
    phase,
    selectedOption,
    collection,
    collectedCount,
    progressPct,
    mascotMessage,
    showCollection,
    setShowCollection,
    showFinalChallenge,
    setShowFinalChallenge,
    isLevelCompleted,
    // Acciones
    checkAnswer,
    nextQuestion,
    retryAfterHint,
    resetLevel,
    speak,
    showMascot,
  };
}